import sessionStore from '../../stores/session-store'
import SessionAPI from '../data/session-api-client'
import BaseAPI from '../data/base-api-client.js'
import {
    REJECTED_SESSION_OUTCOME,
    hasUsableToken,
    isRejectedSessionError,
} from '../utils/session-rejection.js'
import { clearExtensionAuth } from '../utils/extension-installation.js'
import { reset as posthogReset } from '../../utilities/use-posthog.js'
import { clearSentryUser } from '../../utilities/use-sentry.js'

import { createSignal } from 'solid-js'
import { clearQrOnboardingSession } from '../../stores/qr-onboarding-session-store.js'

const [
    signInPromptRequest,
    setSignInPromptRequest,
] = createSignal()

export default class SessionService {
    // TODO: Re-evaluate the use of this service and how it interacts with the store
    static #isAuthenticatedSignal = createSignal(false)
    static isAuthenticated = SessionService.#isAuthenticatedSignal[0]
    static #setIsAuthenticated = SessionService.#isAuthenticatedSignal[1]

    /** Avoid nested delete/logout when EXT storage echoes revoke during WEB-initiated clear */
    static #sessionMutationLock = false

    /**
     * Tracks whether the current `delete()` call is a user-initiated logout.
     * Consumers (e.g. `useAuthGuard`'s createEffect) read this to skip the
     * "session expired, bounce to /?login=true&from=..." redirect when the
     * user chose to log out themselves. Reset in `delete()`'s finally block.
     */
    static #isLoggingOut = false
    static get isLoggingOut () { return SessionService.#isLoggingOut }

    /**
     * Tracks whether the auth signal is about to flip to false because another
     * browser tab logged out (detected via the `storage` event). Consumers
     * (e.g. `useAuthGuard`'s createEffect) read this to redirect to `/` cleanly
     * instead of bouncing through `/?login=true&from=...` and re-opening the
     * login modal. Set/cleared around `checkAuthentication()` in
     * `useAuthSync`'s storage handler.
     */
    static #isCrossTabLogout = false
    static get isCrossTabLogout () { return SessionService.#isCrossTabLogout }
    static markCrossTabLogout () { SessionService.#isCrossTabLogout = true }
    static clearCrossTabLogout () { SessionService.#isCrossTabLogout = false }

    /**
     * TENANT-5504: the latest request to prompt sign-in because the stored session was rejected.
     * `undefined` until the first one. Each request is a fresh object so a repeat request re-notifies.
     * The auth-modal provider reacts to it; services and utils cannot open the modal themselves.
     */
    static signInPromptRequest = signInPromptRequest

    /** In-flight clears keyed by the rejected token — concurrent 401s for one token share a clear. */
    static #rejectedSessionClears = new Map()

    /**
     * How many in-flight operations will prompt sign-in themselves on a 401 (e.g. the booking card,
     * which must first save the stay as a booking intent). While non-zero the generic prompt is
     * skipped, so the guest never gets an intent-less modal they could act on first.
     */
    static #callerOwnedSignInPromptDepth = 0

    /**
     * Set when the auth signal flips to false because the API rejected the session. The route
     * guard reads it to leave the sign-in prompt to `signInPromptRequest` (which redirects on a
     * guarded route itself, carrying any booking intent) instead of redirecting a second time.
     * Cleared once a session is established again.
     */
    static #isRejectedSessionSignOut = false
    static get isRejectedSessionSignOut () { return SessionService.#isRejectedSessionSignOut }

    /**
     * Set by `useAuthGuard` once the page has a guard that redirects on session loss. The sign-in
     * prompt then goes through that redirect (`/?login=true&from=...`) rather than the modal.
     * Page-lifetime, matching the guard's own ownerless effect.
     */
    static #hasGuardedRoute = false
    static get hasGuardedRoute () { return SessionService.#hasGuardedRoute }
    static markGuardedRoute () { SessionService.#hasGuardedRoute = true }

    /**
     * Ask the app to prompt sign-in. With a booking intent token the prompt carries it through
     * OAuth, so the saved stay request resumes once the guest is signed in again.
     */
    static requestSignInPrompt ({
        bookingIntentToken,
    } = {}) {
        if (bookingIntentToken !== undefined && (typeof bookingIntentToken !== 'string' || bookingIntentToken === '')) {
            throw new TypeError('SessionService.requestSignInPrompt requires a non-empty booking intent token when one is given.')
        }
        setSignInPromptRequest({
            bookingIntentToken,
        })
    }

    /**
     * Run an authenticated operation whose caller prompts sign-in itself when the session is
     * rejected. The session is still cleared by the rejection; only the generic prompt is skipped.
     */
    static async withCallerOwnedSignInPrompt (operation) {
        if (typeof operation !== 'function') {
            throw new TypeError('SessionService.withCallerOwnedSignInPrompt requires an operation.')
        }
        SessionService.#callerOwnedSignInPromptDepth += 1
        try {
            return await operation()
        } finally {
            SessionService.#callerOwnedSignInPromptDepth -= 1
        }
    }

    /**
     * Whether `error` is a 401 that left the guest signed out — the only case in which a caller
     * should keep its work as an intent and prompt sign-in. A 401 while a usable session is stored
     * (one signed in after the rejected request was sent) is not: the caller falls through.
     */
    static async isSignedOutAfterRejection (error) {
        if (!isRejectedSessionError(error)) return false
        return !hasUsableToken(await sessionStore.getAccessToken())
    }

    /**
     * The API answered 401 to a request that carried `rejectedToken` (registered with `BaseAPI`).
     * Clears the stored session locally — no `DELETE /session`, the API already refuses it — so
     * the header and account menu show signed-out, then prompts sign-in.
     *
     * The stored token is compared with `rejectedToken` immediately before the clear, so a session
     * signed in while the request was in flight is kept.
     *
     * @returns {Promise<string>} a `REJECTED_SESSION_OUTCOME` value.
     */
    static async handleRejectedSession (rejectedToken) {
        const inFlightClear = SessionService.#rejectedSessionClears.get(rejectedToken)
        if (inFlightClear) return inFlightClear

        const clear = (async () => {
            const storedToken = await sessionStore.getAccessToken()
            if (storedToken !== rejectedToken) {
                return hasUsableToken(storedToken)
                    ? REJECTED_SESSION_OUTCOME.NEWER_SESSION_KEPT
                    : REJECTED_SESSION_OUTCOME.ALREADY_SIGNED_OUT
            }
            await sessionStore.clear()

            SessionService.#isRejectedSessionSignOut = true
            clearExtensionAuth()
            SessionService.#setIsAuthenticated(false)
            posthogReset()
            clearSentryUser()

            if (SessionService.#callerOwnedSignInPromptDepth === 0) {
                SessionService.requestSignInPrompt()
            }
            return REJECTED_SESSION_OUTCOME.CLEARED
        })()
        SessionService.#rejectedSessionClears.set(rejectedToken, clear)
        try {
            return await clear
        } finally {
            SessionService.#rejectedSessionClears.delete(rejectedToken)
        }
    }

    static async store (email, password) {
        try {
            const {
                access_token,
            } = await SessionAPI.store({
                email,
                password,
            })
            await sessionStore.setAccessToken(access_token)
            SessionService.#isRejectedSessionSignOut = false
            this.#setIsAuthenticated(true)
            return access_token
        } catch (error) {
            console.error('SessionAPI.store - Error:', error)
            throw error
        }
    }

    static async delete ({
        intentional = false,
    } = {}) {
        if (this.#sessionMutationLock) return
        this.#sessionMutationLock = true
        if (intentional) this.#isLoggingOut = true
        try {
            clearExtensionAuth()
            await SessionAPI.delete()
            await sessionStore.removeAccessToken()
            this.#setIsAuthenticated(false)
            posthogReset()
            clearSentryUser()
        } finally {
            this.#sessionMutationLock = false
            this.#isLoggingOut = false
        }
    }

    static async checkAuthentication () {
        const token = await sessionStore.getAccessToken()
        if (token) SessionService.#isRejectedSessionSignOut = false
        this.#setIsAuthenticated(Boolean(token))
    }

    /**
     * Complete logout functionality that handles both Core API and local logout
     */
    static async logout () {
        if (this.#sessionMutationLock) return
        this.#sessionMutationLock = true
        try {
            // TODO add logout from Core API

            clearExtensionAuth()
            await sessionStore.removeAccessToken()
            // A shared conference device: the next host must not inherit the QR session.
            clearQrOnboardingSession()
            this.#setIsAuthenticated(false)
            posthogReset()
            clearSentryUser()
        } catch (error) {
            console.error('Logout error:', error)
            throw error
        } finally {
            this.#sessionMutationLock = false
        }
    }

    /**
     * Logs the current user out and refreshes the in-memory auth state so
     * cross-cutting consumers (the top-level nav, route guards, etc.) update
     * immediately. Single shared helper used by every newsroom logout site so
     * the two-step clear+checkAuthentication dance lives in exactly one place.
     *
     * Plan §1.7a — consolidates the inconsistent logout call sites in the
     * newsroom public and admin controllers.
     */
    static async logoutAndNotify () {
        await sessionStore.clear()
        await SessionService.checkAuthentication()
        posthogReset()
        clearSentryUser()
    }
}

BaseAPI.setRejectedSessionHandler((rejectedToken) => SessionService.handleRejectedSession(rejectedToken))
